import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Custom hook to manage subscription-related information and quota checks
 * 
 * @returns {Object} Subscription information and quota status
 */
export const useSubscription = () => {
    // Get subscription from Redux store
    const subscription = useSelector((state) => state.auth.subscription);
    const quota = subscription?.quota;

    // Calculate various subscription statuses
    const subscriptionInfo = useMemo(() => {
        if (!subscription || !quota) {
            return {
                hasSubscription: false,
                isActive: false,
                isTrial: false,
                canUploadCourse: false,
                isStorageExhausted: false,
                isCoursesExhausted: false,
                isMediaHoursExhausted: false,
                isAnyQuotaExhausted: false,
                remainingDays: 0,
                coursesRemaining: 0,
                storageRemaining: 0,
                mediaHoursRemaining: 0,
                subscription: null,
                quota: null
            };
        }

        // Check if subscription is active
        const isActive = subscription.status === 'active';
        
        // Check if subscription is Trial
        const isTrial = subscription.name?.toLowerCase() === 'trial';
        
        // Check if subscription is Premium (not trial)
        const isPremium = !isTrial && isActive;

        // Check quota exhaustion
        const isCoursesExhausted = quota.courses_uploaded >= quota.course_included;
        const isStorageExhausted = quota.storage_consumed_in_mb >= quota.upload_quota;
        const isMediaHoursExhausted = quota.media_hours_consumed_in_minutes >= quota.media_hours_quota_in_minutes;
        
        // Check if any quota is exhausted
        const isAnyQuotaExhausted = isCoursesExhausted || isStorageExhausted || isMediaHoursExhausted;

        // Calculate remaining quotas
        const coursesRemaining = Math.max(0, quota.course_included - quota.courses_uploaded);
        const storageRemaining = Math.max(0, quota.upload_quota - quota.storage_consumed_in_mb);
        const mediaHoursRemaining = Math.max(0, quota.media_hours_quota_in_minutes - quota.media_hours_consumed_in_minutes);

        // Check if user can upload files/courses
        const canUploadCourse = isActive && !isCoursesExhausted && !isStorageExhausted;

        // Calculate remaining days
        const getRemainingDays = () => {
            if (!subscription.end_date) return 0;
            const end = new Date(subscription.end_date);
            const now = new Date();
            const diffTime = end - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return Math.max(diffDays, 0);
        };

        const remainingDays = getRemainingDays();

        // Calculate usage percentages
        const coursesUsagePercentage = quota.course_included > 0 
            ? (quota.courses_uploaded / quota.course_included) * 100 
            : 0;
        const storageUsagePercentage = quota.upload_quota > 0 
            ? (quota.storage_consumed_in_mb / quota.upload_quota) * 100 
            : 0;
        const mediaHoursUsagePercentage = quota.media_hours_quota_in_minutes > 0 
            ? (quota.media_hours_consumed_in_minutes / quota.media_hours_quota_in_minutes) * 100 
            : 0;

        return {
            hasSubscription: true,
            isActive,
            isTrial,
            isPremium,
            canUploadCourse,
            isStorageExhausted,
            isCoursesExhausted,
            isMediaHoursExhausted,
            isAnyQuotaExhausted,
            remainingDays,
            coursesRemaining,
            storageRemaining,
            mediaHoursRemaining,
            coursesUsagePercentage,
            storageUsagePercentage,
            mediaHoursUsagePercentage,
            subscription,
            quota,
            // Helper functions
            isQuotaNearLimit: (type) => {
                const percentages = {
                    courses: coursesUsagePercentage,
                    storage: storageUsagePercentage,
                    mediaHours: mediaHoursUsagePercentage
                };
                return percentages[type] >= 80; // 80% threshold for "near limit"
            },
            getQuotaStatus: (type) => {
                const percentages = {
                    courses: coursesUsagePercentage,
                    storage: storageUsagePercentage,
                    mediaHours: mediaHoursUsagePercentage
                };
                const percentage = percentages[type] || 0;
                if (percentage >= 100) return 'exhausted';
                if (percentage >= 90) return 'critical';
                if (percentage >= 70) return 'warning';
                return 'healthy';
            }
        };
    }, [subscription, quota]);

    return subscriptionInfo;
};

export default useSubscription;

